const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: async (username, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!res.ok) throw new Error('Login failed');
    return res.json();
  },
  
  getMe: async () => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Unauthorized');
    return res.json();
  },
  
  changePassword: async (currentPassword, newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
    });
    if (!res.ok) {
      const error = await res.json();
      throw { response: { data: error } };
    }
    return res.json();
  },
  
  // Categories
  getCategories: async () => {
    const res = await fetch(`${API_URL}/api/categories`);
    return res.json();
  },
  
  createCategory: async (data) => {
    const res = await fetch(`${API_URL}/api/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create category');
    return res.json();
  },
  
  updateCategory: async (id, data) => {
    const res = await fetch(`${API_URL}/api/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update category');
    return res.json();
  },
  
  deleteCategory: async (id) => {
    const res = await fetch(`${API_URL}/api/categories/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete category');
    return res.json();
  },
  
  // Brands
  getBrands: async (categoryId) => {
    const url = categoryId ? `${API_URL}/api/brands?category_id=${categoryId}` : `${API_URL}/api/brands`;
    const res = await fetch(url);
    return res.json();
  },
  
  getBrandBySlug: async (slug) => {
    const res = await fetch(`${API_URL}/api/brands/slug/${slug}`);
    if (!res.ok) throw new Error('Brand not found');
    return res.json();
  },
  
  createBrand: async (data) => {
    const res = await fetch(`${API_URL}/api/brands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create brand');
    return res.json();
  },
  
  updateBrand: async (id, data) => {
    const res = await fetch(`${API_URL}/api/brands/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update brand');
    return res.json();
  },
  
  deleteBrand: async (id) => {
    const res = await fetch(`${API_URL}/api/brands/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete brand');
    return res.json();
  },
  
  // Products
  getProducts: async (brandId, categoryId) => {
    let url = `${API_URL}/api/products?`;
    if (brandId) url += `brand_id=${brandId}&`;
    if (categoryId) url += `category_id=${categoryId}`;
    const res = await fetch(url);
    return res.json();
  },
  
  createProduct: async (data) => {
    const res = await fetch(`${API_URL}/api/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create product');
    return res.json();
  },
  
  updateProduct: async (id, data) => {
    const res = await fetch(`${API_URL}/api/products/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update product');
    return res.json();
  },
  
  deleteProduct: async (id) => {
    const res = await fetch(`${API_URL}/api/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete product');
    return res.json();
  },
  
  // PDFs
  getPDFs: async (brandId) => {
    const url = brandId ? `${API_URL}/api/pdfs?brand_id=${brandId}` : `${API_URL}/api/pdfs`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    return res.json();
  },
  
  createPDF: async (data) => {
    const res = await fetch(`${API_URL}/api/pdfs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create PDF record');
    return res.json();
  },

  updatePDF: async (id, data) => {
    const res = await fetch(`${API_URL}/api/pdfs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update PDF');
    return res.json();
  },
  
  deletePDF: async (id) => {
    const res = await fetch(`${API_URL}/api/pdfs/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete PDF');
    return res.json();
  },
  
  // Projects CRUD
  createProject: async (data) => {
    const res = await fetch(`${API_URL}/api/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create project');
    return res.json();
  },
  
  updateProject: async (id, data) => {
    const res = await fetch(`${API_URL}/api/projects/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update project');
    return res.json();
  },
  
  deleteProject: async (id) => {
    const res = await fetch(`${API_URL}/api/projects/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete project');
    return res.json();
  },
  
  // Media CRUD
  createMedia: async (data) => {
    const res = await fetch(`${API_URL}/api/media`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create media');
    return res.json();
  },
  
  updateMedia: async (id, data) => {
    const res = await fetch(`${API_URL}/api/media/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update media');
    return res.json();
  },
  
  deleteMedia: async (id) => {
    const res = await fetch(`${API_URL}/api/media/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete media');
    return res.json();
  },
  
  // Certificates CRUD
  createCertificate: async (data) => {
    const res = await fetch(`${API_URL}/api/certificates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create certificate');
    return res.json();
  },
  
  updateCertificate: async (id, data) => {
    const res = await fetch(`${API_URL}/api/certificates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update certificate');
    return res.json();
  },
  
  deleteCertificate: async (id) => {
    const res = await fetch(`${API_URL}/api/certificates/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete certificate');
    return res.json();
  },
  
  // Customers CRUD
  createCustomer: async (data) => {
    const res = await fetch(`${API_URL}/api/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create customer');
    return res.json();
  },
  
  updateCustomer: async (id, data) => {
    const res = await fetch(`${API_URL}/api/customers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update customer');
    return res.json();
  },
  
  deleteCustomer: async (id) => {
    const res = await fetch(`${API_URL}/api/customers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete customer');
    return res.json();
  },
  
  // Projects
  getProjects: async () => {
    const res = await fetch(`${API_URL}/api/projects`);
    return res.json();
  },
  
  // Media
  getMedia: async () => {
    const res = await fetch(`${API_URL}/api/media`);
    return res.json();
  },
  
  // Certificates
  getCertificates: async () => {
    const res = await fetch(`${API_URL}/api/certificates`);
    return res.json();
  },
  
  // Customers
  getCustomers: async () => {
    const res = await fetch(`${API_URL}/api/customers`);
    return res.json();
  },
  
  // Stats
  getDashboardStats: async () => {
    const res = await fetch(`${API_URL}/api/stats/dashboard`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Unauthorized');
    return res.json();
  },
  
  // File upload
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/upload/image`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },
  
  uploadPDF: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/upload/pdf`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
  },

  // Quote Requests Management
  getQuotes: async (status) => {
    const url = status ? `${API_URL}/api/admin/quotes?status=${status}` : `${API_URL}/api/admin/quotes`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch quotes');
    return res.json();
  },

  updateQuoteStatus: async (id, status) => {
    const res = await fetch(`${API_URL}/api/admin/quotes/${id}?status=${status}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to update quote status');
    return res.json();
  },

  deleteQuote: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/quotes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete quote');
    return res.json();
  },

  // Contact Messages Management
  getMessages: async (status) => {
    const url = status ? `${API_URL}/api/admin/messages?status=${status}` : `${API_URL}/api/admin/messages`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Failed to fetch messages');
    return res.json();
  },

  updateMessageStatus: async (id, status) => {
    const res = await fetch(`${API_URL}/api/admin/messages/${id}?status=${status}`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to update message status');
    return res.json();
  },

  deleteMessage: async (id) => {
    const res = await fetch(`${API_URL}/api/admin/messages/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete message');
    return res.json();
  },

  // CMS Content Management
  getCMSSection: async (section) => {
    const res = await fetch(`${API_URL}/api/cms/${section}`);
    return res.json();
  },

  getCMSItem: async (section, key) => {
    const res = await fetch(`${API_URL}/api/cms/${section}/${key}`);
    if (!res.ok) throw new Error('CMS content not found');
    return res.json();
  },

  createCMSContent: async (data) => {
    const res = await fetch(`${API_URL}/api/cms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create CMS content');
    return res.json();
  },

  updateCMSContent: async (section, key, data) => {
    const res = await fetch(`${API_URL}/api/cms/${section}/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update CMS content');
    return res.json();
  },

  deleteCMSContent: async (section, key) => {
    const res = await fetch(`${API_URL}/api/cms/${section}/${key}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete CMS content');
    return res.json();
  },

  bulkUpdateCMS: async (updates) => {
    const res = await fetch(`${API_URL}/api/cms/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(updates)
    });
    if (!res.ok) throw new Error('Failed to bulk update CMS');
    return res.json();
  },

  // Timeline/Journey Management
  getTimeline: async () => {
    const res = await fetch(`${API_URL}/api/timeline`);
    return res.json();
  },

  getTimelineEntry: async (id) => {
    const res = await fetch(`${API_URL}/api/timeline/${id}`);
    if (!res.ok) throw new Error('Timeline entry not found');
    return res.json();
  },

  createTimelineEntry: async (data) => {
    const res = await fetch(`${API_URL}/api/timeline`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create timeline entry');
    return res.json();
  },

  updateTimelineEntry: async (id, data) => {
    const res = await fetch(`${API_URL}/api/timeline/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update timeline entry');
    return res.json();
  },

  deleteTimelineEntry: async (id) => {
    const res = await fetch(`${API_URL}/api/timeline/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!res.ok) throw new Error('Failed to delete timeline entry');
    return res.json();
  }
};
